import { ToastContainer } from "react-toastify";
import { Global, css } from "@emotion/react";
import "react-toastify/dist/ReactToastify.css";
import { useRoutes, Navigate } from "react-router-dom"; // Added Navigate
import "./App.css";
import Confetti from "react-confetti";
import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState, lazy } from "react";
import { useDispatch } from "react-redux";
import { Typography } from "antd"; // Import for inline components

import adminRoutes from "./routes/adminRoutes";
import { PublicRoute } from "./routes/PublicRoutes";
import { RequireCustomerPasswordChange } from "./routes/RequireCustomerPasswordChange";
import { LazyWrapper } from "./components/Performance/LazyWrapper";

// CRITICAL: Import the auth restoration action
import { setCustomerCredentials } from "@/redux/slices/customerAuthSlice";
import CustomerProfile from "./pages/CustomerFlow/Profile/CustomerProfile";

// Lazy load components
const SignUp = lazy(() => import("./pages/CustomerFlow/onboarding/SignUp"));
const SFSSignInPage = lazy(
  () => import("./pages/CustomerFlow/onboarding/SfsSignIn"),
);
const SFSForgotPassword = lazy(
  () => import("./pages/CustomerFlow/onboarding/SfsForgotPassword"),
);
const SFSEnterOtp = lazy(
  () => import("./pages/CustomerFlow/onboarding/SfsEnterOtp"),
);
const SFSNewPassword = lazy(
  () => import("./pages/CustomerFlow/onboarding/SfsChangePassword"),
);
const SFSPasswordResetSuccess = lazy(
  () => import("./pages/CustomerFlow/onboarding/SfsPasswordResetSuccess"),
);
const SFSOnboarding = lazy(
  () => import("./pages/CustomerFlow/onboarding/Sfsonboarding"),
);
const SFSForgotPasswordEnterOtp = lazy(
  () => import("./pages/CustomerFlow/onboarding/SfsFPEnterOtp"),
);
const SFSFirstLoginChangePassword = lazy(
  () => import("./pages/CustomerFlow/onboarding/SfsFirstLoginChangePassword"),
);
const SFSLandingPage = lazy(
  () => import("./pages/CustomerFlow/Landing_Page/SfsLandingPage"),
);

// New modular dashboard & pages
const CustomerLayout = lazy(() => import("./layout/CustomerLayout"));
const BulkOrder = lazy(() => import("./pages/CustomerFlow/BulkOrder"));
const EventCatering = lazy(() => import("./pages/CustomerFlow/EventCatering"));
const MyOrder = lazy(() => import("./pages/CustomerFlow/MyOrder"));

const SFSCheckoutPage = lazy(
  () => import("./pages/CustomerFlow/sfsCheckoutPage/SfsCheckoutPage"),
);
const CustomizeMenu = lazy(
  () =>
    import("./pages/CustomerFlow/EventCatering/customizeMenu/CustomizeMenu"),
);

const GlobalStyles = () => (
  <Global
    styles={css`
      body {
        font-family: "Noto Sans", sans-serif;
        color: #333;
      }
    `}
  />
);

function App() {
  const dispatch = useDispatch();
  const [confettiActive, setConfettiActive] = useState(false);
  const [windowDimension, setDimension] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // CRITICAL: Restore authentication state on app mount
  useEffect(() => {
    const restoreAuth = () => {
      try {
        const storedAuth = localStorage.getItem("customerAuthData");
        if (storedAuth) {
          const parsedAuth = JSON.parse(storedAuth);
          dispatch(
            setCustomerCredentials({
              user: parsedAuth.user,
              access_token: parsedAuth.access_token,
            }),
          );
          console.log("✅ Customer auth restored from localStorage");
        }
      } catch (error) {
        console.error("❌ Failed to restore customer auth:", error);
      }
    };
    restoreAuth();
  }, [dispatch]);

  const detectSize = () => {
    setDimension({ width: window.innerWidth, height: window.innerHeight });
  };

  useEffect(() => {
    window.addEventListener("resize", detectSize);
    return () => {
      window.removeEventListener("resize", detectSize);
    };
  }, [windowDimension]);

  useEffect(() => {
    if (confettiActive) {
      setTimeout(() => {
        setConfettiActive(false);
      }, 15000);
    }
  }, [confettiActive]);

  const routing = useRoutes([
    // Public routes
    {
      element: <PublicRoute />,
      children: [
        {
          path: "/",
          element: (
            <LazyWrapper loadingMessage="Loading landing page...">
              <SFSLandingPage />
            </LazyWrapper>
          ),
        },
        {
          path: "/signup",
          element: (
            <LazyWrapper loadingMessage="Loading signup...">
              <SignUp />
            </LazyWrapper>
          ),
        },
        {
          path: "/sfssignin",
          element: (
            <LazyWrapper loadingMessage="Loading sign in...">
              <SFSSignInPage />
            </LazyWrapper>
          ),
        },
        {
          path: "/sfsEnterOtp",
          element: (
            <LazyWrapper loadingMessage="Loading OTP verification...">
              <SFSEnterOtp />
            </LazyWrapper>
          ),
        },
        {
          path: "/sfsForgotPasswordEnterOtp",
          element: (
            <LazyWrapper loadingMessage="Loading password reset...">
              <SFSForgotPasswordEnterOtp />
            </LazyWrapper>
          ),
        },
        {
          path: "/sfsforgotpassword",
          element: (
            <LazyWrapper loadingMessage="Loading forgot password...">
              <SFSForgotPassword />
            </LazyWrapper>
          ),
        },
        {
          path: "/sfsChangePassword",
          element: (
            <LazyWrapper loadingMessage="Loading change password...">
              <SFSNewPassword />
            </LazyWrapper>
          ),
        },
        {
          path: "/sfsPasswordResetSuccess",
          element: (
            <LazyWrapper loadingMessage="Loading success page...">
              <SFSPasswordResetSuccess />
            </LazyWrapper>
          ),
        },
        {
          path: "/sfsonboarding",
          element: (
            <LazyWrapper loadingMessage="Loading onboarding...">
              <SFSOnboarding />
            </LazyWrapper>
          ),
        },
      ],
    },

    // Standalone route: reachable regardless of auth/first-login state so it
    // never gets caught in the RequireCustomerPasswordChange redirect loop below.
    {
      path: "/sfs-change-password-first-login",
      element: (
        <LazyWrapper loadingMessage="Loading...">
          <SFSFirstLoginChangePassword />
        </LazyWrapper>
      ),
    },

    // Protected / App Routes
    {
      element: <RequireCustomerPasswordChange />,
      children: [
        {
          path: "/sfsdashboard",
          element: (
            <LazyWrapper loadingMessage="Loading dashboard...">
              <CustomerLayout
                setConfettiActive={setConfettiActive}
                confettiActive={confettiActive}
              />
            </LazyWrapper>
          ),
          // NESTED CHILD ROUTES for Dashboard Sidebar
          children: [
            {
              index: true, // Matches /sfsdashboard
              element: (
                <LazyWrapper loadingMessage="Loading bulk order...">
                  <BulkOrder />
                </LazyWrapper>
              ),
            },
            {
              path: "catering", // Matches /sfsdashboard/catering
              element: (
                <LazyWrapper loadingMessage="Loading catering...">
                  <EventCatering activeSection="catering-main" />
                </LazyWrapper>
              ),
            },
            {
              path: "orders", // Matches /sfsdashboard/orders
              element: (
                <LazyWrapper loadingMessage="Loading orders...">
                  <MyOrder />
                </LazyWrapper>
              ),
            },
            {
              path: "profile", // Matches /sfsdashboard/profile
              element: (
                <LazyWrapper loadingMessage="Loading profile...">
                  <CustomerProfile />
                </LazyWrapper>
              ),
            },
            {
              path: "wallet", // Matches /sfsdashboard/wallet
              element: (
                <div style={{ textAlign: "center", padding: 64 }}>
                  <Typography.Title level={3}>Wallet</Typography.Title>
                  <Typography.Text>
                    Your wallet information will appear here.
                  </Typography.Text>
                </div>
              ),
            },
          ],
        },
        {
          path: "/sfsCheckOutPage",
          element: (
            <LazyWrapper loadingMessage="Loading checkout...">
              <SFSCheckoutPage />
            </LazyWrapper>
          ),
        },
        // --- NEW PAYMENT VERIFY ROUTE ---
        {
          path: "/sfsCheckOutPage/verify",
          element: (
            <LazyWrapper loadingMessage="Verifying payment...">
              <SFSCheckoutPage />
            </LazyWrapper>
          ),
        },
        {
          path: "/customize_menu",
          element: (
            <LazyWrapper loadingMessage="Loading customize menu...">
              <CustomizeMenu
                setConfettiActive={setConfettiActive}
                confettiActive={confettiActive}
              />
            </LazyWrapper>
          ),
        },
      ],
    },

    // Admin routes
    ...adminRoutes,

    // Fallback
    {
      path: "*",
      element: <Navigate to="/signup" replace />,
    },
  ]);

  return (
    <div>
      <GlobalStyles />
      <ToastContainer position="top-center" autoClose={7000} />
      {confettiActive && (
        <Confetti
          width={windowDimension.width}
          height={windowDimension.height}
          tweenDuration={1000}
        />
      )}
      {routing}
    </div>
  );
}

export default App;
